import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CaretDown, Lightning, TrendUp, WarningCircle, Prohibit } from "@phosphor-icons/react";

const STRENGTH = {
  strong: { bar: "#059669", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  moderate: { bar: "#C5A059", chip: "bg-[#C5A059]/10 text-[#8a6d2f] border-[#C5A059]/30" },
  weak: { bar: "#94a3b8", chip: "bg-slate-100 text-slate-600 border-slate-200" },
};

const factorLabel = (t, f) => t(`matching.factor.${f.code}`, { ...(f.params || {}), defaultValue: f.code });

export function MatchStrengthChip({ strength }) {
  const { t } = useTranslation();
  const s = STRENGTH[strength] || STRENGTH.weak;
  return (
    <span data-testid={`match-strength-${strength}`}
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border rounded-sm ${s.chip}`}>
      {t(`matching.strength.${strength}`, strength)}
    </span>
  );
}

function FactorList({ t, items, tone, sym, label, Icon }) {
  const c = { emerald: "text-emerald-600", amber: "text-amber-600", red: "text-red-500" }[tone];
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wide mb-1 flex items-center gap-1 ${c}`}><Icon size={12} /> {label}</div>
      <ul className="space-y-1">
        {items.map((f, i) => (
          <li key={i} className="text-xs text-slate-600 flex gap-1.5"><span className={c}>{sym}</span>{factorLabel(t, f)}</li>
        ))}
      </ul>
    </div>
  );
}

export function MatchRow({ name, subtitle, match, testid = "match" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const s = STRENGTH[match.strength] || STRENGTH.weak;
  return (
    <div className="p-4 border border-slate-100 rounded-sm hover:bg-slate-50/60 transition-colors" data-testid={testid}>
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 truncate">{name}</div>
          {subtitle && <div className="text-xs text-slate-500 truncate">{subtitle}</div>}
        </div>
        <div className="w-40 flex-shrink-0">
          <div className="flex justify-between items-center mb-1"><MatchStrengthChip strength={match.strength} /><span className="text-xs font-semibold text-[#0B2545]" data-testid={`${testid}-score`}>{match.score}%</span></div>
          <div className="h-2 bg-slate-100 rounded-sm overflow-hidden"><div className="h-full rounded-sm" style={{ width: `${match.score}%`, backgroundColor: s.bar, transition: "width 0.5s ease" }} /></div>
        </div>
      </div>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-why-toggle`}
        className="mt-2 flex items-center gap-1 text-xs font-medium text-[#0B2545] hover:text-[#C5A059] transition-colors">
        <Lightning size={13} weight="fill" className="text-[#C5A059]" /> {t("matching.whyMatch", "Why this match?")}
        <CaretDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 border-t border-slate-100 pt-3 space-y-3" data-testid={`${testid}-why-panel`}>
          <div className="text-xs text-slate-500">{t("scoring.confidence", "Confidence")}: <b className="text-slate-700">{match.confidence}%</b></div>
          {match.positive?.length > 0 && <FactorList t={t} items={match.positive} tone="emerald" sym="+" label={t("matching.positive", "Why it fits")} Icon={TrendUp} />}
          {match.weak?.length > 0 && <FactorList t={t} items={match.weak} tone="amber" sym="~" label={t("matching.weak", "Weak / missing")} Icon={WarningCircle} />}
          {match.disqualifying?.length > 0 && <FactorList t={t} items={match.disqualifying} tone="red" sym="✕" label={t("matching.disqualifying", "Disqualifiers")} Icon={Prohibit} />}
          <div className="bg-[#0B2545]/5 rounded-sm px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{t("scoring.nextAction", "Recommended action")}</div>
            <div className="text-xs font-medium text-[#0B2545] mt-0.5" data-testid={`${testid}-action`}>{t(`matching.action.${match.action_code}`, match.action_code)}</div>
          </div>
          <div className="text-[10px] text-slate-400 italic">{t("matching.rulesBased", "Rule-based match — not an AI prediction.")}</div>
        </div>
      )}
    </div>
  );
}
